import PayoutChargedIncompleteAlert from '@/components/payouts/failure/PayoutChargedIncompleteAlert';
import PayoutDeclinedAlert from '@/components/payouts/failure/PayoutDeclinedAlert';
import PayoutTransfersFailedAlert from '@/components/payouts/failure/PayoutTransfersFailedAlert';
import {
  CHARGED_TRANSFERS_INCOMPLETE,
  PAYMENT_DECLINED,
  resolvePayoutDisplayState,
} from '@/components/payouts/payoutDisplayState';
import { isTransferNeverSent } from '@/components/payouts/transferDisplayState';
import type { InvolvedAffiliate, Transfer } from '@/types/payout';
import {
  PayoutBatchStatus,
  type PayoutBatchClosedReasonValue,
  PayoutTransferStatus,
} from '@fluenceur/types';
import React from 'react';

interface PayoutFailureAlertProps {
  status: PayoutBatchStatus;
  /** Instant the brand's payment was accepted, null while it was not. */
  fundedAt: string | null;
  /** Instant of the last refused payment attempt, null when none was refused. */
  lastPaymentFailedAt: string | null;
  /** The provider's reason for the last refusal, null when it gave none. */
  lastPaymentFailureCode: string | null;
  /** Why an unpaid payout was closed, null or absent while it is open or was paid. */
  closedReason?: PayoutBatchClosedReasonValue | null;
  transfers: Transfer[];
  involvedAffiliates: InvolvedAffiliate[];
}

/**
 * Picks the alert the batch actually deserves. The display state is the only
 * thing consulted, so the drawer cannot contradict the badge.
 */
const PayoutFailureAlert: React.FC<PayoutFailureAlertProps> = ({
  status,
  fundedAt,
  lastPaymentFailedAt,
  lastPaymentFailureCode,
  closedReason = null,
  transfers,
  involvedAffiliates,
}) => {
  const displayState = resolvePayoutDisplayState({
    status,
    fundedAt,
    lastPaymentFailedAt,
    closedReason,
  });

  // A refused attempt on a batch that stays payable: no transfer was even tried.
  if (displayState === PAYMENT_DECLINED) {
    return <PayoutDeclinedAlert failureCode={lastPaymentFailureCode} />;
  }

  // A transfer an unpaid payout's closure stopped was never attempted, so it is
  // no failure to report, whatever the batch around it says.
  const failedTransfers = transfers.filter(
    (transfer) => transfer.status === PayoutTransferStatus.FAILED && !isTransferNeverSent(transfer)
  );

  if (failedTransfers.length === 0) {
    return null;
  }

  if (displayState === CHARGED_TRANSFERS_INCOMPLETE) {
    return (
      <PayoutChargedIncompleteAlert
        failedTransfers={failedTransfers}
        totalTransfers={transfers.length}
        involvedAffiliates={involvedAffiliates}
      />
    );
  }

  if (
    displayState === PayoutBatchStatus.FAILED ||
    displayState === PayoutBatchStatus.PARTIALLY_COMPLETED
  ) {
    return (
      <PayoutTransfersFailedAlert
        failedTransfers={failedTransfers}
        totalTransfers={transfers.length}
        involvedAffiliates={involvedAffiliates}
        lastPaymentFailedAt={lastPaymentFailedAt}
        lastPaymentFailureCode={lastPaymentFailureCode}
      />
    );
  }

  return null;
};

export default PayoutFailureAlert;
